/** Shared components (UX-SPEC §12.7): built once, used by every view. Text is always rendered as Preact text nodes. */
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { clamp100 } from './format.ts';
import { HEADER, STATE_TEXT } from './strings.ts';

/* ---------------- icons (inline SVG, decorative: the text carries the meaning) ---------------- */

type IconName = 'error' | 'notice' | 'info' | 'chevron' | 'check' | 'external' | 'menu';

const PATHS: Record<IconName, string> = {
	error: 'M8 1.5 15 14H1L8 1.5Zm0 4.5v4m0 1.6v.4',
	notice: 'M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Zm0 3.5v4m0 1.6v.4',
	info: 'M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Zm0 5v5m0-7.4v.4',
	chevron: 'm4 6 4 4 4-4',
	check: 'm3 8.5 3.2 3.2L13 5',
	external: 'M6 3H3v10h10v-3M9 3h4v4M13 3 7 9',
	menu: 'M2 4h12M2 8h12M2 12h12',
};

export function Icon({ name }: { name: IconName }): JSX.Element {
	return (
		<svg
			class="ico"
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			stroke-width="1.5"
			stroke-linecap="round"
			stroke-linejoin="round"
			aria-hidden="true"
			focusable="false"
		>
			<path d={PATHS[name]} />
		</svg>
	);
}

/* ---------------- notice ---------------- */

export function Notice({
	kind,
	children,
	live,
}: {
	kind: 'error' | 'notice' | 'info';
	children: ComponentChildren;
	/** Announce politely when it appears (errors always announce as alerts). */
	live?: boolean;
}): JSX.Element {
	const role = kind === 'error' ? 'alert' : live ? 'status' : undefined;
	return (
		<div class={`note-box note-${kind}`} role={role}>
			<Icon name={kind === 'error' ? 'error' : kind === 'notice' ? 'notice' : 'info'} />
			<div>
				{kind === 'error' ? <strong>{STATE_TEXT.errorLabel} </strong> : null}
				{children}
			</div>
		</div>
	);
}

/* ---------------- whisker: a 0–100 track with an interval and a point ---------------- */

export function Whisker({
	value,
	lo,
	hi,
	width = 120,
	label,
	hollow = false,
}: {
	value: number;
	lo: number;
	hi: number;
	width?: number;
	label?: string;
	hollow?: boolean;
}): JSX.Element {
	const x = (v: number) => (clamp100(v) / 100) * (width - 8) + 4;
	return (
		<svg
			class="whisker"
			width={width}
			height="16"
			viewBox={`0 0 ${width} 16`}
			role={label ? 'img' : undefined}
			aria-label={label}
			aria-hidden={label ? undefined : 'true'}
			focusable="false"
		>
			<line x1="4" y1="8" x2={width - 4} y2="8" stroke="var(--stone-300)" stroke-width="1" />
			<line x1={x(lo)} y1="8" x2={x(hi)} y2="8" stroke="var(--ev-500)" stroke-width="2" />
			<line x1={x(lo)} y1="4" x2={x(lo)} y2="12" stroke="var(--ev-500)" stroke-width="1.5" />
			<line x1={x(hi)} y1="4" x2={x(hi)} y2="12" stroke="var(--ev-500)" stroke-width="1.5" />
			<circle
				cx={x(value)}
				cy="8"
				r="3.5"
				fill={hollow ? 'var(--sand-50)' : 'var(--ev-700)'}
				stroke="var(--ev-700)"
				stroke-width="1.5"
			/>
		</svg>
	);
}

/* ---------------- tabs (intake) ---------------- */

export function Tabs<T extends string>({
	label,
	tabs,
	value,
	onChange,
}: {
	label: string;
	tabs: ReadonlyArray<{ id: T; label: string }>;
	value: T;
	onChange: (v: T) => void;
}): JSX.Element {
	const onKey = (e: KeyboardEvent) => {
		const i = tabs.findIndex((t) => t.id === value);
		let n = i;
		if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
		else if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
		else if (e.key === 'Home') n = 0;
		else if (e.key === 'End') n = tabs.length - 1;
		else return;
		e.preventDefault();
		const next = tabs[n];
		if (next) {
			onChange(next.id);
			(document.getElementById(`tab-${next.id}`) as HTMLElement | null)?.focus();
		}
	};
	return (
		<div class="tabs" role="tablist" aria-label={label} onKeyDown={onKey}>
			{tabs.map((t) => (
				<button
					type="button"
					role="tab"
					id={`tab-${t.id}`}
					key={t.id}
					class={`tab${t.id === value ? ' is-on' : ''}`}
					aria-selected={t.id === value}
					aria-controls={`panel-${t.id}`}
					tabIndex={t.id === value ? 0 : -1}
					onClick={() => onChange(t.id)}
				>
					{t.label}
				</button>
			))}
		</div>
	);
}

/* ---------------- modal dialog (native <dialog>) ---------------- */

let dialogSeq = 0;

export function Dialog({
	title,
	open,
	onClose,
	children,
	actions,
}: {
	title: string;
	open: boolean;
	onClose: () => void;
	children: ComponentChildren;
	actions: ComponentChildren;
}): JSX.Element {
	const ref = useRef<HTMLDialogElement>(null);
	// one id per dialog: several are in the DOM at once, and a shared id named every dialog after the first
	const titleId = useRef(`dialog-title-${++dialogSeq}`).current;
	useEffect(() => {
		const d = ref.current;
		if (!d) return;
		if (open && !d.open) d.showModal();
		if (!open && d.open) d.close();
	}, [open]);
	return (
		<dialog ref={ref} class="dialog" aria-labelledby={titleId} onClose={onClose} onCancel={onClose}>
			<h2 id={titleId}>{title}</h2>
			<div class="dialog-body">{children}</div>
			<div class="dialog-actions">{actions}</div>
		</dialog>
	);
}

/* ---------------- menu button (Export) ---------------- */

export function MenuButton({
	label,
	items,
}: {
	label: string;
	items: ReadonlyArray<{ id: string; label: string; busy?: boolean; onSelect: () => void }>;
}): JSX.Element {
	const [open, setOpen] = useState(false);
	const root = useRef<HTMLDivElement>(null);
	// a layout effect: the listeners must exist as soon as the menu is on screen (a passive effect can lag a frame)
	useLayoutEffect(() => {
		if (!open) return;
		const away = (e: MouseEvent) => {
			if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
		};
		// Escape closes the menu wherever focus is (Safari does not focus a button on click)
		const onEscape = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return;
			setOpen(false);
			(root.current?.querySelector('button.menu-trigger') as HTMLElement | null)?.focus();
		};
		document.addEventListener('mousedown', away);
		document.addEventListener('keydown', onEscape);
		return () => {
			document.removeEventListener('mousedown', away);
			document.removeEventListener('keydown', onEscape);
		};
	}, [open]);
	return (
		<div class="menu" ref={root}>
			<button
				type="button"
				class="btn btn-secondary menu-trigger"
				aria-haspopup="true"
				aria-expanded={open}
				onClick={() => setOpen(!open)}
			>
				{label} <Icon name="chevron" />
			</button>
			{open ? (
				<ul class="menu-list">
					{items.map((it) => (
						<li key={it.id}>
							<button
								type="button"
								class="menu-item"
								aria-disabled={it.busy ? 'true' : undefined}
								onClick={() => {
									if (it.busy) return;
									setOpen(false);
									it.onSelect();
								}}
							>
								{it.busy ? HEADER.preparing : it.label}
							</button>
						</li>
					))}
				</ul>
			) : null}
		</div>
	);
}

/* ---------------- virtual table rows ---------------- */

/**
 * Windowed rows for a fixed row height: returns the first and last row to render for a scroll container, plus the
 * spacer heights. Rendering only `[start, end)` keeps the DOM small for 10,000-row lists (UX-HOT-01, UX-PERF-04).
 */
export function useWindow(
	count: number,
	rowHeight: number,
	overscan = 12
): {
	ref: { current: HTMLDivElement | null };
	start: number;
	end: number;
	before: number;
	after: number;
	scrollToRow: (i: number) => void;
} {
	const ref = useRef<HTMLDivElement | null>(null);
	const [range, setRange] = useState({ top: 0, height: 600 });
	const frame = useRef(0);
	const update = () => {
		frame.current = 0;
		const el = ref.current;
		if (!el) return;
		setRange((r) =>
			r.top === el.scrollTop && r.height === el.clientHeight
				? r
				: { top: el.scrollTop, height: el.clientHeight }
		);
	};
	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		update();
		const onScroll = () => {
			if (!frame.current) frame.current = requestAnimationFrame(update);
		};
		el.addEventListener('scroll', onScroll, { passive: true });
		const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(onScroll) : null;
		ro?.observe(el);
		return () => {
			el.removeEventListener('scroll', onScroll);
			ro?.disconnect();
			if (frame.current) cancelAnimationFrame(frame.current);
		};
	});
	const first = Math.max(0, Math.floor(range.top / rowHeight) - overscan);
	const last = Math.min(count, Math.ceil((range.top + range.height) / rowHeight) + overscan);
	return {
		ref,
		start: first,
		end: Math.max(first, last),
		before: first * rowHeight,
		after: Math.max(0, (count - Math.max(first, last)) * rowHeight),
		scrollToRow: (i: number) => {
			const el = ref.current;
			if (el) el.scrollTop = Math.max(0, i * rowHeight - el.clientHeight / 3);
		},
	};
}

export const ROW_H = 40;

/** A link styled as a button that changes the hash (native: Enter works, Back works). */
export function LinkButton({
	href,
	children,
	label,
	secondary,
}: {
	href: string;
	children: ComponentChildren;
	label?: string;
	secondary?: boolean;
}): JSX.Element {
	return (
		<a class={`btn ${secondary ? 'btn-secondary' : 'btn-primary'}`} href={href} aria-label={label}>
			{children}
		</a>
	);
}

/** A group of toggle buttons (a fieldset with a hidden legend, so the group has a name). */
export function Seg({
	label,
	children,
}: { label: string; children: ComponentChildren }): JSX.Element {
	return (
		<fieldset class="seg">
			<legend class="visually-hidden">{label}</legend>
			{children}
		</fieldset>
	);
}

/** True below the 768 px breakpoint of the phone layout (UX-SPEC §14). */
export function useMobile(): boolean {
	const query = '(max-width: 767px)';
	const [m, setM] = useState(() => typeof matchMedia === 'function' && matchMedia(query).matches);
	useEffect(() => {
		const mq = matchMedia(query);
		const on = () => setM(mq.matches);
		mq.addEventListener('change', on);
		return () => mq.removeEventListener('change', on);
	}, []);
	return m;
}
